import React from "react";
import * as Lucide from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BRAND, TIERS } from "@/content/landing";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const { red: RED, gold: GOLD, charcoal: CHARCOAL } = BRAND.colors;

const startLogin = (intendedRoute) => {
  const redirectUrl = window.location.origin + (intendedRoute || "/dashboard");
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const trackEvent = (event) => {
  axios.post(`${BACKEND}/api/metrics/event`, { event }).catch(() => {});
};

function Icon({ name, ...props }) {
  const C = Lucide[name] || Lucide.Sparkles;
  return <C {...props} />;
}

const BENEFITS = [
  { icon: "Megaphone",  title: "List Your Products",       desc: "Showcase your products with photos, videos and a full opportunity profile page." },
  { icon: "Users",      title: "Recruit Affiliates",       desc: "Build a nationwide affiliate sales force — zero-stock, commission-based." },
  { icon: "Store",      title: "Recruit Stockists",        desc: "Find serious operators ready to hold inventory and grow your reach." },
  { icon: "Truck",      title: "Recruit Distributors",     desc: "Build regional and national distribution faster than going alone." },
  { icon: "Sparkles",   title: "Recruit Influencers",      desc: "Connect with vetted creators ready to promote your brand." },
  { icon: "TrendingUp", title: "Find Investors",           desc: "Get discovered by qualified investors looking for businesses to back." },
  { icon: "Handshake",  title: "Find Strategic Partners",  desc: "Cross-promotions, JV deals and ecosystem partnerships." },
  { icon: "Shield",     title: "Verified Trust Layer",     desc: "Verification badges and metrics that signal credibility to applicants." },
];

const HOW_IT_WORKS = [
  { step: "01", title: "Apply as Vendor",     desc: "Submit your business overview & program details — our team reviews within 48 hours." },
  { step: "02", title: "Build Your Profile",  desc: "Upload products, videos, testimonials and define opportunity types (Affiliate / Agent / Stockist / etc.)." },
  { step: "03", title: "Launch & Recruit",    desc: "Get featured to MIRESCO's network. Applications flow directly into your vendor dashboard." },
  { step: "04", title: "Scale & Grow",        desc: "Manage your sales network, training and pay-outs from one dashboard." },
];

const SUCCESS_METRICS = [
  { label: "Vendor Applications",   value: "200+",  hint: "in pipeline" },
  { label: "Affiliate Network",     value: "320+",  hint: "active" },
  { label: "States Covered",        value: "16",    hint: "Malaysia-wide" },
  { label: "Avg. Onboarding Time",  value: "48hr",  hint: "to verified" },
];

export default function Vendor() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-[#09090b]">
      {/* HEADER (simplified, links back to /) */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-3" data-testid="vendor-brand-link">
            <img src={BRAND.logo} alt={BRAND.name} className="w-10 h-10 object-contain" />
            <div className="leading-tight text-left">
              <div className="font-display font-black text-base tracking-tight">{BRAND.name}</div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-500">{BRAND.tagline}</div>
            </div>
          </button>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-zinc-700">
            <button onClick={() => navigate("/")} className="hover:text-[#C8102E]">Home</button>
            <a href="#benefits" className="hover:text-[#C8102E]">Benefits</a>
            <a href="#how" className="hover:text-[#C8102E]">How it works</a>
            <a href="#plans" className="hover:text-[#C8102E]">Plans</a>
          </nav>
          <button
            onClick={() => { trackEvent("vendor_header_apply"); startLogin("/portal/business_owner"); }}
            data-testid="vendor-header-apply"
            className="px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
            style={{ background: RED }}
          >
            Apply as Vendor
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-200" style={{ background: CHARCOAL, color: "white" }}>
        <div className="absolute inset-0 opacity-20" style={{
          background: `radial-gradient(circle at 75% 50%, ${GOLD} 0%, transparent 60%)`
        }} />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 rise">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 border border-white/30" data-testid="vendor-hero-tag">
              <span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> MIRESCO Vendor Program
            </div>
            <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]" data-testid="vendor-hero-headline">
              Grow your business with a <span style={{ color: GOLD }}>nationwide sales network.</span>
            </h1>
            <p className="mt-6 text-white/80 text-base sm:text-lg max-w-2xl" data-testid="vendor-hero-sub">
              List your products on MIRESCO and recruit affiliates, agents, stockists, distributors and influencers — all from one dashboard. Find investors and strategic partners while you scale.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="vendor-hero-ctas">
              <button
                onClick={() => { trackEvent("vendor_hero_apply"); startLogin("/portal/business_owner"); }}
                data-testid="vendor-hero-apply"
                className="inline-flex items-center gap-2 text-white px-6 py-3 font-semibold hover:opacity-90 transition-opacity"
                style={{ background: RED }}
              >
                Apply as Vendor <Lucide.ArrowRight className="w-4 h-4" />
              </button>
              <a href="#plans" className="inline-flex items-center gap-2 bg-white text-zinc-900 px-6 py-3 font-semibold hover:bg-zinc-200 transition-colors">
                View Plans
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3" data-testid="vendor-metrics">
              {SUCCESS_METRICS.map((m) => (
                <div key={m.label} className="bg-white/10 backdrop-blur border border-white/15 p-5">
                  <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">{m.label}</div>
                  <div className="font-display font-black text-3xl mt-2" style={{ color: GOLD }}>{m.value}</div>
                  <div className="text-xs text-white/60 mt-1">{m.hint}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: RED }} /> Vendor benefits</div>
              <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">Everything a vendor needs.</h2>
            </div>
            <p className="max-w-md text-zinc-600">
              MIRESCO replaces 8 different tools — recruitment, listing, sales-team management, training, payouts.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="vendor-benefits-grid">
            {BENEFITS.map((b, i) => (
              <div
                key={b.title}
                data-testid={`vendor-benefit-${i}`}
                className="border border-zinc-200 p-6 hover:border-[#C8102E] hover:shadow-[6px_6px_0_0_#C8102E] transition-all bg-white"
              >
                <div className="w-12 h-12 grid place-items-center text-white" style={{ background: i % 2 === 0 ? RED : CHARCOAL }}>
                  <Icon name={b.icon} className="w-6 h-6" />
                </div>
                <h3 className="font-display font-black text-lg mt-5 tracking-tight">{b.title}</h3>
                <p className="text-sm text-zinc-600 mt-2 leading-snug">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-b border-zinc-200" style={{ background: "#FAFAFA" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <div className="mb-12">
            <div className="tag"><span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> How it works</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">From application to first applicant — in 48 hours.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="vendor-how-grid">
            {HOW_IT_WORKS.map((s) => (
              <div key={s.step} className="bg-white border border-zinc-200 p-6">
                <div className="font-display font-black text-5xl tracking-tighter" style={{ color: RED }}>{s.step}</div>
                <h3 className="font-display font-black text-lg tracking-tight mt-3">{s.title}</h3>
                <p className="text-sm text-zinc-600 mt-2 leading-snug">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANS — re-uses TIERS from content config */}
      <section id="plans" className="border-b border-zinc-200" style={{ background: CHARCOAL, color: "white" }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <div className="tag border-white/20" style={{ color: "white" }}>
                <span className="w-1.5 h-1.5 inline-block" style={{ background: GOLD }} /> Vendor plans
              </div>
              <h2 className="font-display font-black text-3xl sm:text-5xl mt-4 tracking-tight">Pick your plan.</h2>
            </div>
            <p className="max-w-md text-zinc-300">
              Free to apply. Upgrade to Pro or Elite for vendor tools, priority listings and territory rights.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="vendor-plans-grid">
            {TIERS.map((t) => (
              <div
                key={t.key}
                data-testid={`vendor-plan-${t.key}`}
                className={`relative p-7 transition-all ${t.popular ? "lg:-translate-y-2 shadow-2xl" : ""}`}
                style={{ background: "white", color: CHARCOAL, border: `2px solid ${t.popular ? GOLD : "transparent"}` }}
              >
                {t.popular && (
                  <div className="absolute -top-3 left-7 text-[10px] font-bold uppercase tracking-widest px-3 py-1" style={{ background: GOLD, color: CHARCOAL }}>
                    Most Popular
                  </div>
                )}
                <div className="w-12 h-12 grid place-items-center text-white" style={{ background: t.accent }}>
                  <Icon name={t.icon} className="w-6 h-6" />
                </div>
                <h3 className="font-display font-black text-2xl mt-5 tracking-tight">{t.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display font-black text-4xl tracking-tight">{t.price}</span>
                  <span className="text-sm text-zinc-500">{t.per}</span>
                </div>
                <ul className="mt-6 space-y-2">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-700">
                      <Lucide.Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: t.accent }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => { trackEvent("vendor_plan_" + t.key); startLogin("/membership"); }}
                  data-testid={`vendor-plan-cta-${t.key}`}
                  className="mt-7 w-full text-sm font-semibold py-3 transition-opacity hover:opacity-90"
                  style={{ background: t.accent, color: t.key === "elite" ? CHARCOAL : "white" }}
                >
                  Get {t.name.split(" ")[0]}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden" style={{ background: RED, color: "white" }}>
        <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 80% 50%, ${GOLD} 0%, transparent 60%)` }} />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-20 text-center">
          <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tight" data-testid="vendor-final-cta-heading">
            Ready to grow your business?
          </h2>
          <p className="mt-5 text-white/90 text-base sm:text-lg max-w-2xl mx-auto">
            Apply as a MIRESCO vendor and start recruiting your nationwide sales network.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => { trackEvent("vendor_final_apply"); startLogin("/portal/business_owner"); }}
              data-testid="vendor-final-apply"
              className="inline-flex items-center gap-2 bg-white px-6 py-3 font-semibold hover:bg-zinc-100 transition-colors"
              style={{ color: RED }}
            >
              Apply as Vendor <Lucide.ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/showcase/inochi")}
              data-testid="vendor-final-view-showcase"
              className="inline-flex items-center gap-2 px-6 py-3 font-semibold border-2 border-white/70 text-white hover:bg-white hover:text-[#C8102E] transition-colors"
            >
              See INOCHI Showcase Example
            </button>
          </div>
        </div>
      </section>

      {/* compact footer */}
      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 py-6 flex flex-wrap items-center justify-between gap-3 text-xs text-zinc-500">
          <button onClick={() => navigate("/")} className="hover:text-[#C8102E]">← Back to homepage</button>
          <span>© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
